/**
 * Dedicated ULTEM complete-case configurator.
 *
 * The current product is the only case merchandise source. Variant state is
 * loaded per size through Shopify's option_values rendering path so this file
 * never depends on the first 250 entries of product.variants.
 */
(function () {
  'use strict';

  window.theme = window.theme || {};
  if (window.theme.ultemV2) return;

  var CONFIG_SELECTOR = '[data-ultem-v2-config]';
  var instances = {};
  var STEP_ORDER = ['1', '2', '3', '4'];
  var DOTW_DAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
  var NANO_POCKET_OPTIONS = ['AM', 'PM', 'LUNCH', 'EXTRA', 'SUN', 'MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT'];
  var MISSION_POCKET_OPTIONS = ['AM-LUNCH-PM', 'FRI-SAT-SUN', 'AM-PM-EXTRA'];
  var LID_ALLOWED_PATTERN = /^[\w\s!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]*$/;
  // The lid listing also sells aluminum lids of the same sizes; Ultem lids are this SKU family.
  var ULTEM_LID_SKU_PREFIX = 'ENG-LID-ULTEM-';

  function parseConfig(element) {
    try {
      return JSON.parse(element.textContent || '');
    } catch (error) {
      if (window.console && typeof console.error === 'function') {
        console.error('[ultem-v2] Could not parse section configuration.', error);
      }
      return null;
    }
  }

  function normalize(value) {
    return String(value || '')
      .trim()
      .toLowerCase()
      .replace(/['"]/g, '')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');
  }

  function normalizeWords(value) {
    return String(value || '')
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, ' ')
      .trim()
      .replace(/\s+/g, ' ');
  }

  // "Ultem Lid - Weekly Pill Case" and "Weekly Pill Case (Set of 2)" both key as "weekly pill case".
  function addonSizeKey(value) {
    return normalizeWords(value).replace(/^ultem lid /, '').replace(/ set of 2$/, '');
  }

  function parseCents(value) {
    var cents = parseInt(value, 10);
    return isFinite(cents) ? cents : 0;
  }

  function readInlineJson(id) {
    var element = document.getElementById(id);
    if (!element || !String(element.textContent || '').trim()) return null;
    try {
      return JSON.parse(element.textContent);
    } catch (error) {
      if (window.console && typeof console.warn === 'function') {
        console.warn('[ultem-v2] Could not parse #' + id + '.', error);
      }
      return null;
    }
  }

  function createInstance(cfg) {
    var root = document.getElementById('ultem-v2-builder-' + cfg.sectionId);
    if (!root) return null;

    var sizeList = root.querySelector('[data-size-list]');
    var sizeStatus = root.querySelector('[data-size-status]');
    var colorList = root.querySelector('[data-color-list]');
    var lidControls = root.querySelector('[data-lid-controls]');
    var pocketControls = root.querySelector('[data-pocket-controls]');
    var lidHelp = root.querySelector('[data-lid-help-text]');
    var pocketHelp = root.querySelector('[data-pocket-help-text]');
    var priceEl = root.querySelector('[data-price]');
    var comparePriceEl = root.querySelector('[data-compare-price]');
    var stockPill = root.querySelector('[data-stock-pill]');
    var stockLabel = root.querySelector('[data-stock-label]');
    var addButton = root.querySelector('[data-add-to-cart]');
    var addLabel = root.querySelector('[data-add-to-cart-label]');
    var addSpinner = root.querySelector('[data-add-to-cart-spinner]');
    var addError = root.querySelector('[data-add-to-cart-error]');
    var addMessage = root.querySelector('[data-add-to-cart-message]');
    var mainSlide = root.querySelector('[data-gallery-slide][data-slide-index="0"]');
    var mainImg = root.querySelector('[data-primary-media]');
    var mainThumb = root.querySelector('[data-gallery-thumb][data-slide-target="0"]');
    var thumbImg = root.querySelector('[data-primary-thumb-image]');
    var initialPrimaryMedia = root.querySelector('[data-initial-primary-media]');
    var initialPrimaryThumb = root.querySelector('[data-initial-primary-thumb]');
    var mainComposite = root.querySelector('[data-primary-lid-composite]');
    var thumbComposite = root.querySelector('[data-primary-lid-thumb-composite]');
    var mainPreviewLayer = root.querySelector('[data-lid-engraving-preview="gallery"]');
    var thumbPreviewLayer = root.querySelector('[data-lid-engraving-preview="thumb"]');
    var engravingPreviewLayers = root.querySelectorAll('[data-lid-engraving-preview]');
    var galleryStage = root.querySelector('[data-gallery-stage]');
    var galleryPrev = root.querySelector('[data-gallery-prev]');
    var galleryNext = root.querySelector('[data-gallery-next]');
    var caseSpecPanels = root.querySelectorAll('[data-case-spec-panel]');
    var lidProduct = readInlineJson('ultem-v2-lid-product-json-' + cfg.sectionId);
    var pocketProduct = readInlineJson('ultem-v2-pocket-product-json-' + cfg.sectionId);
    var pocketImages = readInlineJson('ultem-v2-pocket-images-json-' + cfg.sectionId) || {};
    var pocketBodyPreview = root.querySelector('[data-pocket-body-preview]');
    var loadToken = 0;
    var activeGalleryIndex = 0;
    var swipeState = null;
    var tooltip = null;
    var tooltipTrigger = null;
    var submitting = false;
    var destroyed = false;
    var openSizePopover = null;
    var openColorPopover = null;
    var selectorPopoverListenersBound = false;
    var selectorPopoverSeq = 0;
    var FOCUS_RING_BLACK = 'tw:focus:outline-none tw:focus-visible:ring-2 tw:focus-visible:ring-black tw:focus-visible:ring-offset-1';
    var FOCUS_RING_INSET = 'tw:focus:outline-none tw:focus-visible:bg-gray-50 tw:focus-visible:ring-2 tw:focus-visible:ring-inset tw:focus-visible:ring-gray-950';

    var state = {
      pendingSize: null,
      size: null,
      colors: [],
      colorKey: '',
      variant: null,
      sizePreview: null,
      loading: false,
      loadError: '',
      lid: { mode: null, text: '' },
      pocket: { mode: null, value: '' },
      lidMatch: { variant: null, error: '' },
      pocketMatch: { variant: null, error: '' },
      touched: { '1': false, '2': false, '3': false, '4': false },
      expanded: null
    };

    function formatMoney(cents) {
      var amount = parseCents(cents);
      if (window.theme && window.theme.Currency && typeof window.theme.Currency.formatMoney === 'function') {
        return window.theme.Currency.formatMoney(amount, window.theme.moneyFormat || cfg.moneyFormat);
      }
      if (window.Shopify && typeof window.Shopify.formatMoney === 'function') {
        return window.Shopify.formatMoney(amount, cfg.moneyFormat);
      }
      return '$' + (amount / 100).toFixed(2);
    }

    function formatAddonPrice(variant) {
      var cents = parseCents(variant && variant.price);
      return cents > 0 ? '(+' + formatMoney(cents) + ')' : '';
    }

    function getSizeById(id) {
      var value = String(id || '');
      var sizes = Array.isArray(cfg.sizeOptions) ? cfg.sizeOptions : [];
      for (var i = 0; i < sizes.length; i++) {
        if (String(sizes[i].id) === value) return sizes[i];
      }
      return null;
    }

    function getSizeByKey(key) {
      var value = normalize(key);
      var sizes = Array.isArray(cfg.sizeOptions) ? cfg.sizeOptions : [];
      for (var i = 0; i < sizes.length; i++) {
        if (normalize(sizes[i].key) === value || normalize(sizes[i].label) === value) return sizes[i];
      }
      return null;
    }

    function getColorByKey(key) {
      var value = normalize(key);
      for (var i = 0; i < state.colors.length; i++) {
        if (state.colors[i].key === value) return state.colors[i];
      }
      return null;
    }

    function getSelectedColor() {
      return getColorByKey(state.colorKey);
    }

    function getStep(step) {
      return root.querySelector('[data-step="' + step + '"]');
    }

    function getStepHeader(step) {
      return root.querySelector('[data-step-header="' + step + '"]');
    }

    function getStepBody(step) {
      return root.querySelector('[data-step-body="' + step + '"]');
    }

    function getValidation(step) {
      if (step === '1') return root.querySelector('[data-size-validation]');
      if (step === '2') return root.querySelector('[data-color-validation]');
      if (step === '3') return root.querySelector('[data-lid-validation]');
      if (step === '4') return root.querySelector('[data-pocket-validation]');
      return null;
    }

    function setValidation(step, message) {
      var element = getValidation(step);
      if (!element) return;
      var text = String(message || '').trim();
      element.textContent = text;
      element.classList.toggle('tw:hidden', !text);
    }

    function setSummary(step, value) {
      var element = root.querySelector('[data-step-summary="' + step + '"]');
      if (!element) return;
      var text = String(value || '').trim();
      element.innerHTML = '';
      if (!text) {
        element.classList.add('tw:hidden');
        element.removeAttribute('data-summary-value');
        return;
      }
      var prefix = document.createElement('span');
      prefix.className = 'tw:font-normal tw:text-gray-600';
      prefix.textContent = 'Selected: ';
      var selected = document.createElement('span');
      selected.className = 'tw:font-bold tw:text-black';
      selected.textContent = text;
      element.appendChild(prefix);
      element.appendChild(selected);
      element.setAttribute('data-summary-value', text);
      element.setAttribute('aria-label', 'Selected: ' + text);
      element.classList.remove('tw:hidden');
    }

    function applyStepVisual(step) {
      var card = getStep(step);
      var header = getStepHeader(step);
      var body = getStepBody(step);
      if (!card || !header || !body) return;
      var locked = card.getAttribute('data-locked') === 'true';
      var active = state.expanded === step && !locked;
      var touched = !!state.touched[step];

      card.setAttribute('data-active', active ? 'true' : 'false');
      card.setAttribute('data-touched', touched ? 'true' : 'false');
      header.disabled = locked;
      header.setAttribute('aria-expanded', active ? 'true' : 'false');
      header.setAttribute('aria-disabled', locked ? 'true' : 'false');
      body.setAttribute('aria-hidden', active ? 'false' : 'true');
      body.inert = !active;
      body.classList.toggle('tw:grid-rows-[1fr]', active);
      body.classList.toggle('tw:grid-rows-[0fr]', !active);

      card.classList.toggle('tw:border-blue-500', active);
      card.classList.toggle('tw:border-green-500', !active && touched);
      card.classList.toggle('tw:border-gray-300', !active && !touched);
      header.classList.toggle('tw:bg-blue-50', active);
      header.classList.toggle('tw:bg-green-50', !active && touched);

      var chevron = header.querySelector('[data-step-chevron]');
      if (chevron) chevron.classList.toggle('tw:rotate-180', active);
    }

    function setLocked(step, locked) {
      var card = getStep(step);
      if (!card) return;
      card.setAttribute('data-locked', locked ? 'true' : 'false');
      if (locked && state.expanded === step) state.expanded = null;
      applyStepVisual(step);
    }

    function setTouched(step, touched) {
      state.touched[step] = !!touched;
      applyStepVisual(step);
    }

    function collapseAll() {
      state.expanded = null;
      STEP_ORDER.forEach(applyStepVisual);
    }

    function focusHeader(step) {
      var header = getStepHeader(step);
      if (!header || header.disabled || typeof header.focus !== 'function') return;
      if (window.requestAnimationFrame) {
        window.requestAnimationFrame(function () { header.focus(); });
      } else {
        header.focus();
      }
    }

    function expandStep(step, options) {
      options = options || {};
      var card = getStep(step);
      if (!card || card.getAttribute('data-locked') === 'true') return;
      state.expanded = step;
      STEP_ORDER.forEach(applyStepVisual);
      if (step === '3') renderLidControls();
      if (step === '4') renderPocketControls();
      if (options.focus) focusHeader(step);
      refresh();
    }

    function clearAddonState() {
      state.lid = { mode: null, text: '' };
      state.pocket = { mode: null, value: '' };
      state.lidMatch = { variant: null, error: '' };
      state.pocketMatch = { variant: null, error: '' };
      if (lidControls) lidControls.innerHTML = '';
      if (pocketControls) pocketControls.innerHTML = '';
    }

    function resetStepsAfter(step) {
      var start = parseInt(step, 10) + 1;
      if (start <= 3) clearAddonState();
      for (var i = start; i <= 4; i++) {
        var value = String(i);
        state.touched[value] = false;
        setSummary(value, '');
        setValidation(value, '');
        setLocked(value, true);
      }
    }

    function resetForSizeChange() {
      state.size = null;
      state.colors = [];
      state.colorKey = '';
      state.variant = null;
      state.sizePreview = null;
      clearAddonState();
      STEP_ORDER.forEach(function (step) {
        state.touched[step] = false;
        setSummary(step, '');
        setValidation(step, '');
      });
      setLocked('1', false);
      setLocked('2', true);
      setLocked('3', true);
      setLocked('4', true);
      root.removeAttribute('data-selected-variant-id');
    }

    function resetForColorChange() {
      clearAddonState();
      for (var i = 2; i <= 4; i++) {
        var step = String(i);
        state.touched[step] = false;
        setSummary(step, '');
        setValidation(step, '');
      }
      setLocked('3', true);
      setLocked('4', true);
    }

    function renderCaseSpecs() {
      var key = state.pendingSize ? normalize(state.pendingSize.key || state.pendingSize.label) : '';
      for (var i = 0; i < caseSpecPanels.length; i++) {
        caseSpecPanels[i].classList.toggle(
          'tw:hidden',
          !key || normalize(caseSpecPanels[i].getAttribute('data-case-spec-size-key')) !== key
        );
      }
    }

    function setSizeStatus(message, tone) {
      if (!sizeStatus) return;
      var text = String(message || '').trim();
      sizeStatus.innerHTML = '';
      if (text) {
        var statusText = document.createElement('span');
        statusText.textContent = text;
        sizeStatus.appendChild(statusText);
        if (tone === 'error' && state.pendingSize) {
          var retryButton = document.createElement('button');
          retryButton.type = 'button';
          retryButton.setAttribute('data-size-retry', '');
          retryButton.className = 'tw:ml-2 tw:font-semibold tw:underline tw:underline-offset-2';
          retryButton.textContent = 'Try again';
          sizeStatus.appendChild(retryButton);
        }
      }
      sizeStatus.classList.toggle('tw:hidden', !text);
      sizeStatus.classList.toggle('tw:border-red-200', tone === 'error');
      sizeStatus.classList.toggle('tw:bg-red-50', tone === 'error');
      sizeStatus.classList.toggle('tw:text-red-800', tone === 'error');
      sizeStatus.classList.toggle('tw:border-gray-200', tone !== 'error');
      sizeStatus.classList.toggle('tw:bg-gray-50', tone !== 'error');
      sizeStatus.classList.toggle('tw:text-gray-600', tone !== 'error');
    }

    function setStock(status, label) {
      if (!stockPill) return;
      stockPill.setAttribute('data-stock-status', status);
      if (stockLabel) stockLabel.textContent = label;
    }

    function getAddonTotal() {
      var total = 0;
      if (state.lid.mode === 'CUSTOM' && String(state.lid.text || '').trim() && state.lidMatch.variant) {
        total += parseCents(state.lidMatch.variant.price);
      }
      if (state.pocket.mode && state.pocket.mode !== 'NONE' && state.pocket.value && state.pocketMatch.variant) {
        total += parseCents(state.pocketMatch.variant.price);
      }
      return total;
    }

    function renderPrice() {
      if (!state.variant) {
        if (priceEl && cfg.priceDescription) priceEl.textContent = cfg.priceDescription;
        if (comparePriceEl) comparePriceEl.classList.add('tw:hidden');
        return;
      }
      var base = parseCents(state.variant.price);
      var addons = getAddonTotal();
      if (priceEl) priceEl.textContent = formatMoney(base + addons);
      if (comparePriceEl) {
        var compare = parseCents(state.variant.compare_at_price);
        var show = compare > base;
        comparePriceEl.textContent = show ? formatMoney(compare + addons) : '';
        comparePriceEl.classList.toggle('tw:hidden', !show);
      }
    }

    function renderStock() {
      if (state.loading) {
        setStock('checking', 'Checking availability');
        return;
      }
      if (state.variant) {
        setStock(state.variant.available ? 'instock' : 'outstock', state.variant.available ? 'In stock' : 'Out of stock');
        return;
      }
      if (state.pendingSize && state.loadError) {
        setStock('outstock', 'Unavailable');
        return;
      }
      setStock(cfg.initialProductAvailable ? 'instock' : 'outstock', cfg.initialProductAvailable ? 'In stock' : 'Out of stock');
    }

    function ensureImage(parent, selector, attrs) {
      if (!parent) return null;
      var image = parent.querySelector(selector);
      if (image) return image;
      image = document.createElement('img');
      Object.keys(attrs || {}).forEach(function (name) {
        image.setAttribute(name, attrs[name]);
      });
      parent.appendChild(image);
      return image;
    }

    function showCompositeMedia(visible) {
      if (mainComposite) mainComposite.classList.toggle('tw:hidden', !visible);
      if (thumbComposite) thumbComposite.classList.toggle('tw:hidden', !visible);
      if (initialPrimaryMedia) initialPrimaryMedia.classList.toggle('tw:hidden', visible);
      if (initialPrimaryThumb) initialPrimaryThumb.classList.toggle('tw:hidden', visible);
    }

    function updateProductMedia(variant) {
      if (!variant) return;
      var imageUrl = String(variant.featured_image || '').trim();
      var alt = cfg.productTitle + ' — ' + String(variant.option1 || 'selected size');
      var placeholder = mainSlide ? mainSlide.querySelector('[data-primary-placeholder]') : null;

      if (imageUrl) {
        if (placeholder) placeholder.classList.add('tw:hidden');
        mainImg = ensureImage(mainPreviewLayer, '[data-primary-media]', {
          'data-primary-media': '',
          'loading': 'eager',
          'width': '500',
          'height': '125'
        });
        thumbImg = ensureImage(thumbPreviewLayer, '[data-primary-thumb-image]', {
          'data-primary-thumb-image': '',
          'loading': 'lazy',
          'width': '500',
          'height': '125'
        });
        if (mainImg) {
          mainImg.src = imageUrl;
          mainImg.alt = alt;
        }
        if (thumbImg) {
          thumbImg.src = imageUrl;
          thumbImg.alt = alt;
        }
        if (mainThumb) mainThumb.setAttribute('aria-label', alt);
        showCompositeMedia(true);
      } else {
        showCompositeMedia(false);
        if (!placeholder && mainSlide) {
          placeholder = document.createElement('div');
          placeholder.setAttribute('data-primary-placeholder', '');
          placeholder.className = 'tw:flex tw:h-full tw:items-center tw:justify-center tw:px-6 tw:text-center tw:text-sm tw:text-gray-500';
          mainSlide.appendChild(placeholder);
        }
        if (placeholder) {
          placeholder.textContent = 'No variant image is available for ' + String(variant.option1 || 'this size') + '.';
          placeholder.classList.remove('tw:hidden');
        }
      }
      renderEngravingPreview();
      setActiveGallerySlide(0);
    }

    // The engraving is drawn by the per-case rules in theme.css.liquid, keyed on
    // the handleized size, exactly as it is on the other Ultem pages. This page
    // sells single cases only, so data-text1 stays empty.
    function renderEngravingPreview() {
      var size = state.size || state.pendingSize;
      var caseKey = size && size.key ? size.key : '';
      var text = state.lid.mode === 'CUSTOM'
        ? String(state.lid.text || '').slice(0, getLidMaxLength())
        : '';
      for (var i = 0; i < engravingPreviewLayers.length; i++) {
        engravingPreviewLayers[i].setAttribute('data-case', caseKey);
        engravingPreviewLayers[i].setAttribute('data-text', text);
        engravingPreviewLayers[i].setAttribute('data-text1', '');
      }
    }

    function getGalleryCount() {
      return root.querySelectorAll('[data-gallery-slide]').length;
    }

    function setActiveGallerySlide(index) {
      var count = getGalleryCount();
      var next = parseInt(index, 10);
      if (!count) return;
      if (!isFinite(next)) next = 0;
      next = ((next % count) + count) % count;
      activeGalleryIndex = next;
      var slides = root.querySelectorAll('[data-gallery-slide]');
      var thumbs = root.querySelectorAll('[data-gallery-thumb]');
      for (var i = 0; i < slides.length; i++) {
        slides[i].setAttribute('data-active', String(parseInt(slides[i].getAttribute('data-slide-index'), 10) === next));
      }
      for (var j = 0; j < thumbs.length; j++) {
        var active = parseInt(thumbs[j].getAttribute('data-slide-target'), 10) === next;
        thumbs[j].setAttribute('data-active', active ? 'true' : 'false');
        thumbs[j].setAttribute('aria-current', active ? 'true' : 'false');
      }
      if (galleryPrev) galleryPrev.disabled = count <= 1;
      if (galleryNext) galleryNext.disabled = count <= 1;
    }

    function moveGallery(delta) {
      setActiveGallerySlide(activeGalleryIndex + delta);
    }

    function focusElementSoon(element) {
      if (!element || typeof element.focus !== 'function') return;
      var focus = function () {
        try {
          element.focus({ preventScroll: false });
        } catch (error) {
          element.focus();
        }
      };
      if (window.requestAnimationFrame) window.requestAnimationFrame(focus);
      else window.setTimeout(focus, 0);
    }

    function getSelectorPopoverTrigger(popover) {
      if (!popover || !popover.id) return null;
      return root.querySelector('[data-selector-trigger][popovertarget="' + popover.id + '"]');
    }

    function placeSelectorPopover(popover) {
      var trigger = getSelectorPopoverTrigger(popover);
      if (!trigger) return;
      var rect = trigger.getBoundingClientRect();
      var viewportWidth = window.innerWidth || document.documentElement.clientWidth || rect.width;
      var viewportHeight = window.innerHeight || document.documentElement.clientHeight || 0;
      var padding = 16;
      var width = Math.min(rect.width, Math.max(viewportWidth - padding * 2, 0));
      var left = Math.min(Math.max(rect.left, padding), Math.max(viewportWidth - width - padding, padding));

      popover.style.position = 'fixed';
      popover.style.margin = '0';
      popover.style.left = left + 'px';
      popover.style.right = 'auto';
      popover.style.bottom = 'auto';
      popover.style.width = width + 'px';

      var popupHeight = popover.offsetHeight || 0;
      var spaceBelow = viewportHeight - rect.bottom;
      if (spaceBelow < popupHeight + padding && rect.top > popupHeight + padding) {
        popover.style.top = (rect.top - popupHeight - 4) + 'px';
      } else {
        popover.style.top = (rect.bottom + 4) + 'px';
      }
    }

    function placeOpenSelectorPopovers() {
      if (openSizePopover) placeSelectorPopover(openSizePopover);
      if (openColorPopover) placeSelectorPopover(openColorPopover);
    }

    function bindSelectorPopoverListeners() {
      if (selectorPopoverListenersBound) return;
      window.addEventListener('resize', placeOpenSelectorPopovers);
      window.addEventListener('scroll', placeOpenSelectorPopovers, true);
      selectorPopoverListenersBound = true;
    }

    function unbindSelectorPopoverListeners() {
      if (!selectorPopoverListenersBound) return;
      window.removeEventListener('resize', placeOpenSelectorPopovers);
      window.removeEventListener('scroll', placeOpenSelectorPopovers, true);
      selectorPopoverListenersBound = false;
    }

    function closeSelectorPopover(popover) {
      var target = popover || openColorPopover || openSizePopover;
      if (!target) return;
      var trigger = getSelectorPopoverTrigger(target);
      if (trigger) trigger.setAttribute('aria-expanded', 'false');
      if (typeof target.hidePopover === 'function') {
        try {
          target.hidePopover();
        } catch (error) {}
      }
      if (openSizePopover === target) openSizePopover = null;
      if (openColorPopover === target) openColorPopover = null;
      if (!openSizePopover && !openColorPopover) unbindSelectorPopoverListeners();
    }

    function getEnabledSelectorOptions(popover) {
      if (!popover) return [];
      return Array.prototype.slice.call(popover.querySelectorAll('[data-selector-option]')).filter(function (option) {
        return !option.disabled && option.getAttribute('aria-disabled') !== 'true';
      });
    }

    function focusSelectorOption(option) {
      if (!option) return;
      var popover = option.closest('[data-selector-popover]');
      var options = popover ? popover.querySelectorAll('[data-selector-option]') : [];
      for (var i = 0; i < options.length; i++) {
        options[i].setAttribute('tabindex', options[i] === option ? '0' : '-1');
      }
      focusElementSoon(option);
      if (typeof option.scrollIntoView === 'function') option.scrollIntoView({ block: 'nearest' });
    }

    function focusInitialSelectorOption(popover, focusLast) {
      var options = getEnabledSelectorOptions(popover);
      if (!options.length) return;
      var selected = options.find(function (option) {
        return option.getAttribute('aria-selected') === 'true';
      });
      focusSelectorOption(focusLast ? options[options.length - 1] : (selected || options[0]));
    }

    function openSelectorPopoverFromTrigger(trigger, focusLast) {
      if (!trigger) return;
      var targetId = trigger.getAttribute('popovertarget');
      var popover = targetId ? document.getElementById(targetId) : null;
      if (!popover) return;
      if (focusLast) popover.setAttribute('data-focus-last-option', 'true');
      else popover.removeAttribute('data-focus-last-option');
      placeSelectorPopover(popover);
      if (typeof popover.showPopover === 'function') {
        try {
          popover.showPopover();
          return;
        } catch (error) {}
      }
      focusInitialSelectorOption(popover, !!focusLast);
    }

    function onSelectorPopoverBeforeToggle(event) {
      if (event.newState === 'open') placeSelectorPopover(event.target);
    }

    function onSelectorPopoverToggle(event) {
      var type = event.target.getAttribute('data-selector-type');
      var trigger = getSelectorPopoverTrigger(event.target);
      if (event.newState === 'open') {
        if (type === 'size') openSizePopover = event.target;
        if (type === 'color') openColorPopover = event.target;
        placeSelectorPopover(event.target);
        bindSelectorPopoverListeners();
        if (trigger) trigger.setAttribute('aria-expanded', 'true');
        var focusLast = event.target.getAttribute('data-focus-last-option') === 'true';
        event.target.removeAttribute('data-focus-last-option');
        focusInitialSelectorOption(event.target, focusLast);
      } else {
        if (openSizePopover === event.target) openSizePopover = null;
        if (openColorPopover === event.target) openColorPopover = null;
        if (trigger) trigger.setAttribute('aria-expanded', 'false');
        if (!openSizePopover && !openColorPopover) unbindSelectorPopoverListeners();
      }
    }

    function createSizeThumbnail(size, sizeClass) {
      var thumbnail = document.createElement('span');
      thumbnail.className = 'tw:flex tw:shrink-0 tw:items-center tw:justify-center tw:overflow-hidden tw:rounded tw:border tw:border-gray-200 tw:bg-gray-100 ' + (sizeClass || 'tw:h-10 tw:w-10');
      if (size && size.thumbnail) {
        var image = document.createElement('img');
        image.src = size.thumbnail;
        image.alt = '';
        image.loading = 'lazy';
        image.className = 'tw:h-full tw:w-full tw:object-contain tw:p-0.5';
        thumbnail.appendChild(image);
      } else {
        var placeholder = document.createElement('span');
        placeholder.setAttribute('aria-hidden', 'true');
        placeholder.className = 'tw:flex tw:h-full tw:w-full tw:items-center tw:justify-center tw:text-gray-400';
        placeholder.innerHTML = '<svg class="tw:h-5 tw:w-5" viewBox="0 0 24 24" fill="none"><rect x="5" y="3" width="14" height="18" rx="3" stroke="currentColor" stroke-width="1.5"/><path d="M9 8h6M9 12h6M9 16h4" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/></svg>';
        thumbnail.appendChild(placeholder);
      }
      return thumbnail;
    }

    function renderSizeSelector() {
      if (!sizeList) return;
      var selectedSize = state.pendingSize || state.size;
      var renderKey = selectedSize ? String(selectedSize.id) : '';
      if (sizeList.getAttribute('data-render-key') === renderKey) return;
      if (openSizePopover && sizeList.contains(openSizePopover)) closeSelectorPopover(openSizePopover);
      sizeList.innerHTML = '';
      sizeList.setAttribute('data-render-key', renderKey);
      selectorPopoverSeq += 1;
      var popoverId = root.id + '-sizepop-' + selectorPopoverSeq;

      var trigger = document.createElement('button');
      trigger.type = 'button';
      trigger.setAttribute('data-selector-trigger', '');
      trigger.setAttribute('data-selector-type', 'size');
      trigger.setAttribute('data-size-trigger', '');
      trigger.setAttribute('popovertarget', popoverId);
      trigger.setAttribute('aria-haspopup', 'listbox');
      trigger.setAttribute('aria-controls', popoverId);
      trigger.setAttribute('aria-expanded', 'false');
      trigger.className = 'tw:flex tw:min-h-14 tw:w-full tw:items-center tw:justify-between tw:gap-2 tw:rounded-md tw:border tw:border-gray-200 tw:bg-white tw:py-1.5 tw:pl-2 tw:pr-4 tw:text-left tw:transition-all tw:duration-fast tw:ease-standard tw:cursor-pointer tw:hover:border-gray-400 ' + FOCUS_RING_BLACK;

      var triggerContent = document.createElement('span');
      triggerContent.className = 'tw:flex tw:min-w-0 tw:flex-1 tw:items-center tw:gap-3';
      triggerContent.appendChild(createSizeThumbnail(selectedSize, 'tw:h-11 tw:w-11'));
      var triggerName = document.createElement('span');
      triggerName.className = 'tw:min-w-0 tw:flex-1 tw:truncate tw:text-sm tw:font-semibold tw:text-gray-950';
      triggerName.textContent = selectedSize ? selectedSize.label : 'Select case size';
      triggerContent.appendChild(triggerName);
      trigger.appendChild(triggerContent);

      var chevron = document.createElement('span');
      chevron.setAttribute('aria-hidden', 'true');
      chevron.className = 'tw:flex tw:shrink-0 tw:items-center tw:justify-center tw:text-gray-500';
      chevron.innerHTML = '<svg class="tw:h-2 tw:w-3" viewBox="0 0 12 7" fill="none"><path d="M11 1L6 6L1 1" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round"/></svg>';
      trigger.appendChild(chevron);
      sizeList.appendChild(trigger);

      var popover = document.createElement('div');
      popover.id = popoverId;
      popover.setAttribute('popover', 'auto');
      popover.setAttribute('data-selector-popover', '');
      popover.setAttribute('data-selector-type', 'size');
      popover.setAttribute('data-size-popover', '');
      popover.className = 'tw:m-0 tw:border-0 tw:bg-transparent tw:p-0';

      var list = document.createElement('div');
      list.setAttribute('role', 'listbox');
      list.setAttribute('aria-label', 'Case sizes');
      list.className = 'tw:m-0 tw:flex tw:max-h-72 tw:flex-col tw:overflow-y-auto tw:overscroll-contain tw:rounded-lg tw:border tw:border-gray-200 tw:bg-white tw:p-0 tw:shadow-lg';
      (cfg.sizeOptions || []).forEach(function (size) {
        var selected = selectedSize && String(size.id) === String(selectedSize.id);
        var option = document.createElement('button');
        option.type = 'button';
        option.setAttribute('data-selector-option', '');
        option.setAttribute('data-selector-type', 'size');
        option.setAttribute('data-size-option', '');
        option.setAttribute('data-size-option-id', size.id);
        option.setAttribute('role', 'option');
        option.setAttribute('aria-selected', selected ? 'true' : 'false');
        option.setAttribute('data-selected', selected ? 'true' : 'false');
        option.setAttribute('tabindex', '-1');
        option.disabled = !size.available;
        option.className = 'tw:flex tw:min-h-14 tw:w-full tw:items-center tw:gap-3 tw:border-0 tw:bg-transparent tw:px-2 tw:py-1.5 tw:text-left tw:transition-colors tw:duration-fast tw:ease-standard tw:cursor-pointer tw:hover:bg-gray-50 tw:focus:outline-none tw:focus-visible:bg-gray-50 tw:data-[selected=true]:bg-gray-50 tw:disabled:cursor-not-allowed tw:disabled:opacity-40 tw:disabled:hover:bg-transparent';
        option.appendChild(createSizeThumbnail(size, 'tw:h-11 tw:w-11'));

        var label = document.createElement('span');
        label.className = 'tw:min-w-0 tw:flex-1 tw:truncate tw:text-sm tw:font-medium tw:text-gray-900';
        label.textContent = size.label;
        option.appendChild(label);
        if (!size.available) {
          var soldOut = document.createElement('span');
          soldOut.className = 'tw:shrink-0 tw:rounded-full tw:bg-gray-100 tw:px-1.5 tw:py-0.5 tw:text-[10px] tw:font-semibold tw:leading-tight tw:text-gray-600';
          soldOut.textContent = 'Sold out';
          option.appendChild(soldOut);
        }
        var check = document.createElement('span');
        check.setAttribute('aria-hidden', 'true');
        check.className = 'tw:flex tw:h-4 tw:w-4 tw:shrink-0 tw:items-center tw:justify-center tw:text-gray-950' + (selected ? '' : ' tw:hidden');
        check.innerHTML = '<svg class="tw:h-4 tw:w-4" viewBox="0 0 16 16" fill="currentColor"><path d="M6.5 11.4 2.6 7.5 4 6.1l2.5 2.5L12 3.1l1.4 1.4z"/></svg>';
        option.appendChild(check);
        list.appendChild(option);
      });
      popover.appendChild(list);
      popover.addEventListener('beforetoggle', onSelectorPopoverBeforeToggle);
      popover.addEventListener('toggle', onSelectorPopoverToggle);
      sizeList.appendChild(popover);
    }

    function mergeColorMeta(color) {
      var meta = cfg.colorMeta && cfg.colorMeta[color.key];
      if (!meta) return color;
      color.name = meta.name || color.name;
      color.sortIndex = meta.sortIndex;
      color.swatchImage = meta.swatchImage || '';
      color.badges = Array.isArray(meta.badges) ? meta.badges : [];
      return color;
    }

    function buildColorCatalog(payload, size) {
      if (!payload || payload.error) throw new Error('Could not load colors for this size.');
      if (payload.handle !== cfg.productHandle) throw new Error('The returned product does not match this ULTEM case.');
      if (normalizeWords(payload.size) !== normalizeWords(size.label)) throw new Error('The returned size does not match your selection.');
      if (!Array.isArray(payload.options) || !Array.isArray(payload.variants)) throw new Error('The returned variant data is incomplete.');

      var seen = {};
      var colors = payload.options.map(function (option, sourceIndex) {
        var name = String(option.first_color || '').trim();
        var key = normalize(name);
        if (!key || seen[key]) throw new Error('The product contains an ambiguous Color option.');
        seen[key] = true;

        var variants = payload.variants.filter(function (variant) {
          return normalizeWords(variant.option1) === normalizeWords(size.label) && normalizeWords(variant.option2) === normalizeWords(name);
        });
        if (variants.length > 1) throw new Error('The product contains duplicate Size and Color variants.');
        var variant = variants[0] || null;
        return mergeColorMeta({
          key: key,
          name: name,
          sourceIndex: sourceIndex,
          optionValueId: option.option_value_id,
          available: !!(variant && variant.available),
          variant: variant,
          swatchImage: '',
          sortIndex: null,
          badges: []
        });
      });

      colors.sort(function (a, b) {
        if (a.available !== b.available) return a.available ? -1 : 1;
        var aIndex = a.sortIndex === null || a.sortIndex === undefined || a.sortIndex === '' ? null : Number(a.sortIndex);
        var bIndex = b.sortIndex === null || b.sortIndex === undefined || b.sortIndex === '' ? null : Number(b.sortIndex);
        if (aIndex !== null && isFinite(aIndex) && bIndex !== null && isFinite(bIndex) && aIndex !== bIndex) return aIndex - bIndex;
        if (aIndex !== null && isFinite(aIndex) && (bIndex === null || !isFinite(bIndex))) return -1;
        if (bIndex !== null && isFinite(bIndex) && (aIndex === null || !isFinite(aIndex))) return 1;
        return a.sourceIndex - b.sourceIndex;
      });
      return colors;
    }

    function appendBadge(parent, badge) {
      if (!badge || !badge.name) return;
      var element = document.createElement('span');
      element.className = 'tw:rounded-full tw:bg-gray-100 tw:px-1.5 tw:py-0.5 tw:text-[10px] tw:font-medium tw:leading-tight tw:text-gray-700';
      element.textContent = badge.name;
      if (badge.color) {
        element.style.backgroundColor = badge.color;
        element.style.color = '#fff';
      }
      parent.appendChild(element);
    }

    function createColorSwatch(color, sizeClass) {
      var swatch = document.createElement('span');
      swatch.className = 'tw:relative tw:flex tw:shrink-0 tw:items-center tw:justify-center tw:overflow-hidden tw:rounded-full tw:border tw:border-gray-200 tw:bg-gray-100 ' + (sizeClass || 'tw:h-9 tw:w-9');
      if (color && color.swatchImage) {
        var image = document.createElement('img');
        image.src = color.swatchImage;
        image.alt = '';
        image.loading = 'lazy';
        image.className = 'tw:h-full tw:w-full tw:object-cover';
        swatch.appendChild(image);
      }
      if (color && !color.available) {
        var slash = document.createElement('span');
        slash.setAttribute('aria-hidden', 'true');
        slash.className = 'tw:pointer-events-none tw:absolute tw:left-1/2 tw:top-1/2 tw:h-0.5 tw:w-[140%] tw:-translate-x-1/2 tw:-translate-y-1/2 tw:-rotate-45 tw:bg-red-600';
        swatch.appendChild(slash);
      }
      return swatch;
    }

    function appendColorStatus(message, tone) {
      var status = document.createElement('div');
      status.className = tone === 'error'
        ? 'tw:flex tw:flex-col tw:gap-2 tw:rounded tw:border tw:border-red-200 tw:bg-red-50 tw:p-3 tw:text-sm tw:text-red-800'
        : 'tw:rounded tw:border tw:border-gray-200 tw:bg-white tw:p-3 tw:text-sm tw:text-gray-600';
      status.textContent = message;
      colorList.appendChild(status);
    }

    function renderColorSelect() {
      if (!colorList) return;
      var renderKey = [
        state.loading ? 'loading' : 'idle',
        state.loadError,
        state.size ? state.size.id : '',
        state.colorKey,
        state.colors.map(function (color) { return color.key + ':' + (color.available ? '1' : '0'); }).join('|')
      ].join('::');
      if (colorList.getAttribute('data-render-key') === renderKey) return;
      if (openColorPopover && colorList.contains(openColorPopover)) closeSelectorPopover(openColorPopover);
      colorList.innerHTML = '';
      colorList.setAttribute('data-render-key', renderKey);
      colorList.className = 'tw:grid tw:grid-cols-1 tw:gap-2';
      if (state.loading) {
        return;
      }
      if (state.loadError) {
        appendColorStatus(state.loadError, 'error');
        return;
      }
      if (!state.size || !state.colors.length) {
        appendColorStatus('Choose and confirm a size before selecting a color.');
        return;
      }

      var selectedColor = getSelectedColor();
      var control = document.createElement('div');
      control.className = 'tw:flex tw:w-full tw:flex-col tw:gap-2 tw:sm:max-w-md';
      selectorPopoverSeq += 1;
      var popoverId = root.id + '-color-popover-' + selectorPopoverSeq;

      var trigger = document.createElement('button');
      trigger.type = 'button';
      trigger.setAttribute('data-selector-trigger', '');
      trigger.setAttribute('data-selector-type', 'color');
      trigger.setAttribute('data-color-popover-trigger', '');
      trigger.setAttribute('data-color-action', 'single');
      trigger.setAttribute('popovertarget', popoverId);
      trigger.setAttribute('aria-haspopup', 'listbox');
      trigger.setAttribute('aria-expanded', 'false');
      trigger.setAttribute('aria-controls', popoverId);
      trigger.setAttribute('aria-label', 'Color: ' + (selectedColor ? selectedColor.name : 'Select color'));
      trigger.className = 'tw:flex tw:w-full tw:items-center tw:justify-between tw:gap-2 tw:min-h-10 tw:rounded-md tw:border tw:border-gray-200 tw:bg-white tw:py-1.5 tw:pl-3 tw:pr-4 tw:text-left tw:cursor-pointer tw:transition-all tw:duration-fast tw:ease-standard tw:hover:border-gray-400 ' + FOCUS_RING_BLACK;

      var triggerContent = document.createElement('span');
      triggerContent.className = 'tw:flex tw:min-w-0 tw:flex-1 tw:items-center tw:gap-3';
      triggerContent.appendChild(createColorSwatch(selectedColor, 'tw:h-9 tw:w-9'));
      var triggerText = document.createElement('span');
      triggerText.className = 'tw:flex tw:min-w-0 tw:flex-1 tw:flex-wrap tw:items-center tw:gap-1.5';
      var triggerName = document.createElement('span');
      triggerName.className = 'tw:min-w-0 tw:truncate tw:text-sm tw:font-semibold tw:text-gray-950';
      triggerName.textContent = selectedColor ? selectedColor.name : 'Select color';
      triggerText.appendChild(triggerName);
      if (selectedColor) selectedColor.badges.forEach(function (badge) { appendBadge(triggerText, badge); });
      triggerContent.appendChild(triggerText);
      trigger.appendChild(triggerContent);
      var chevron = document.createElement('span');
      chevron.setAttribute('aria-hidden', 'true');
      chevron.className = 'tw:flex tw:shrink-0 tw:items-center tw:justify-center tw:text-gray-500';
      chevron.innerHTML = '<svg class="tw:h-2 tw:w-3" viewBox="0 0 12 7" fill="none"><path d="M11 1L6 6L1 1" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round"></path></svg>';
      trigger.appendChild(chevron);
      control.appendChild(trigger);

      var popover = document.createElement('div');
      popover.id = popoverId;
      popover.setAttribute('popover', 'auto');
      popover.setAttribute('data-selector-popover', '');
      popover.setAttribute('data-selector-type', 'color');
      popover.setAttribute('data-color-popover', '');
      popover.setAttribute('data-color-action', 'single');
      popover.setAttribute('role', 'listbox');
      popover.setAttribute('aria-label', 'Color');
      popover.className = 'tw:m-0 tw:border-0 tw:bg-transparent tw:p-0';
      var list = document.createElement('ul');
      list.className = 'tw:list-none tw:m-0 tw:max-h-72 tw:overflow-hidden tw:overflow-y-auto tw:rounded-lg tw:border tw:border-gray-200 tw:bg-white tw:p-0 tw:shadow-lg';
      state.colors.forEach(function (color) {
        var selected = color.key === state.colorKey;
        var option = document.createElement('li');
        option.setAttribute('data-selector-option', '');
        option.setAttribute('data-selector-type', 'color');
        option.setAttribute('data-color-option-key', color.key);
        option.setAttribute('role', 'option');
        option.setAttribute('aria-selected', selected ? 'true' : 'false');
        option.setAttribute('aria-disabled', color.available ? 'false' : 'true');
        option.setAttribute('aria-label', color.name + (color.available ? '' : ', sold out') + (selected ? ', selected' : ''));
        option.setAttribute('tabindex', '-1');
        option.className = 'tw:flex tw:min-h-10 tw:max-h-12 tw:items-center tw:gap-2 tw:px-3 tw:py-1.5 tw:cursor-pointer tw:transition-colors tw:duration-fast tw:ease-standard tw:hover:bg-gray-50 tw:aria-disabled:cursor-not-allowed tw:aria-disabled:opacity-40 tw:aria-disabled:hover:bg-transparent ' + FOCUS_RING_INSET + (selected ? ' tw:bg-gray-50' : '');
        option.appendChild(createColorSwatch(color, 'tw:h-8 tw:w-8'));
        var labelWrap = document.createElement('span');
        labelWrap.className = 'tw:flex tw:min-w-0 tw:flex-1 tw:flex-wrap tw:items-center tw:gap-1.5';
        var name = document.createElement('span');
        name.className = 'tw:min-w-0 tw:truncate tw:text-sm tw:font-medium tw:text-gray-900';
        name.textContent = color.name;
        labelWrap.appendChild(name);
        color.badges.forEach(function (badge) { appendBadge(labelWrap, badge); });
        if (!color.available) {
          var soldOut = document.createElement('span');
          soldOut.className = 'tw:rounded-full tw:bg-gray-100 tw:px-1.5 tw:py-0.5 tw:text-[10px] tw:font-semibold tw:leading-tight tw:text-gray-600';
          soldOut.textContent = 'Sold out';
          labelWrap.appendChild(soldOut);
        }
        option.appendChild(labelWrap);
        var check = document.createElement('span');
        check.setAttribute('aria-hidden', 'true');
        check.className = 'tw:flex tw:h-4 tw:w-4 tw:flex-shrink-0 tw:items-center tw:justify-center tw:text-black' + (selected ? '' : ' tw:hidden');
        check.innerHTML = '<svg class="tw:h-4 tw:w-4" viewBox="0 0 16 16" fill="currentColor"><path d="M6.5 11.4 2.6 7.5 4 6.1l2.5 2.5L12 3.1l1.4 1.4z"></path></svg>';
        option.appendChild(check);
        list.appendChild(option);
      });
      popover.appendChild(list);
      popover.addEventListener('beforetoggle', onSelectorPopoverBeforeToggle);
      popover.addEventListener('toggle', onSelectorPopoverToggle);
      control.appendChild(popover);
      colorList.appendChild(control);
    }

    function getPreferredColor(key) {
      var preferred = getColorByKey(key);
      if (preferred && preferred.available && preferred.variant) return preferred;
      for (var i = 0; i < state.colors.length; i++) {
        if (state.colors[i].available && state.colors[i].variant) return state.colors[i];
      }
      return null;
    }

    function fetchSizeState(size, options) {
      options = options || {};
      var token = ++loadToken;
      var preferredColorKey = options.preferredColorKey || state.colorKey;
      state.pendingSize = size;
      resetForSizeChange();
      state.pendingSize = size;
      state.loading = true;
      state.loadError = '';
      setSizeStatus('', '');
      renderCaseSpecs();
      refresh();

      var fetcher = window.theme && window.theme.productDataFetcher;
      if (!fetcher || typeof fetcher.fetchOptionState !== 'function') {
        state.loading = false;
        state.loadError = 'Product option data is unavailable. Please refresh the page.';
        setSizeStatus(state.loadError, 'error');
        refresh();
        return Promise.resolve(null);
      }

      return fetcher.fetchOptionState(cfg.productUrl, cfg.optionStateSectionId, [size.id])
        .then(function (payload) {
          if (destroyed || token !== loadToken) return null;
          state.colors = buildColorCatalog(payload, size);
          var representative = null;
          var sourceOrder = state.colors.slice().sort(function (a, b) { return a.sourceIndex - b.sourceIndex; });
          for (var i = 0; i < sourceOrder.length; i++) {
            if (sourceOrder[i].available && sourceOrder[i].variant) {
              representative = sourceOrder[i].variant;
              break;
            }
          }
          state.sizePreview = representative;
          if (representative) updateProductMedia(representative);

          var selectedColor = getPreferredColor(preferredColorKey);
          state.colorKey = selectedColor ? selectedColor.key : '';
          state.variant = selectedColor ? selectedColor.variant : null;
          state.loading = false;
          state.loadError = representative ? '' : 'No available colors were found for this size.';
          setSizeStatus(state.loadError, state.loadError ? 'error' : '');
          if (state.variant) root.setAttribute('data-selected-variant-id', state.variant.id || '');
          refresh();

          if (options.autoConfirm && !state.loadError) {
            confirmStep('1', { focus: false });
            if (options.autoConfirmColor && state.variant && state.colorKey === normalize(options.preferredColorKey)) {
              confirmStep('2', { focus: false });
            }
          }
          return payload;
        })
        .catch(function (error) {
          if (destroyed || token !== loadToken) return null;
          state.loading = false;
          state.colors = [];
          state.variant = null;
          state.loadError = error && error.message ? error.message : 'Could not load this size. Please try again.';
          setSizeStatus(state.loadError, 'error');
          refresh();
          return null;
        });
    }

    function getProductOptionName(product, index) {
      var option = product && product.options && product.options[index];
      if (typeof option === 'string') return option;
      return option && option.name ? option.name : '';
    }

    function resolveAddon(product, sizeLabel, label, skuPrefix) {
      if (!product || !Array.isArray(product.variants)) {
        return { variant: null, error: label + ' is not configured.' };
      }
      var sizeIndex = -1;
      for (var i = 0; i < 3; i++) {
        if (normalizeWords(getProductOptionName(product, i)).indexOf('size') !== -1) {
          sizeIndex = i;
          break;
        }
      }
      if (sizeIndex === -1 && Array.isArray(product.options) && product.options.length === 1) sizeIndex = 0;

      var expected = addonSizeKey(sizeLabel);
      var matches = product.variants.filter(function (variant) {
        if (skuPrefix && String(variant.sku || '').trim().toUpperCase().indexOf(skuPrefix) !== 0) return false;
        var value = sizeIndex >= 0 ? variant['option' + (sizeIndex + 1)] : variant.title;
        return addonSizeKey(value) === expected;
      });
      if (matches.length !== 1) {
        return {
          variant: null,
          error: matches.length > 1
            ? label + ' has duplicate variants for this size.'
            : label + ' is not available for this size.'
        };
      }
      if (!matches[0].available) return { variant: null, error: label + ' is sold out for this size.' };
      return { variant: matches[0], error: '' };
    }

    function resolveAddons() {
      var sizeLabel = state.size ? state.size.label : '';
      state.lidMatch = resolveAddon(lidProduct, sizeLabel, 'Lid engraving', ULTEM_LID_SKU_PREFIX);
      state.pocketMatch = resolveAddon(pocketProduct, sizeLabel, 'Pocket engraving');
    }

    function getLidMaxLength() {
      return normalizeWords(state.size && state.size.label).indexOf('weekly') !== -1 ? 12 : 3;
    }

    function getPocketKind() {
      var size = normalizeWords(state.size && state.size.label);
      if (size.indexOf('weekly') !== -1) return 'weekly';
      if (size.indexOf('mission') !== -1) return 'mission';
      return 'nano';
    }

    function getPocketOptions() {
      var kind = getPocketKind();
      if (kind === 'weekly') return DOTW_DAYS;
      if (kind === 'mission') return MISSION_POCKET_OPTIONS;
      return NANO_POCKET_OPTIONS;
    }

    function getPocketMode() {
      return getPocketKind() === 'weekly' ? 'DOTW' : 'CUSTOM';
    }

    function createModeButton(attribute, mode, label, price, selected, disabled) {
      var button = document.createElement('button');
      button.type = 'button';
      button.setAttribute(attribute, mode);
      button.setAttribute('aria-pressed', selected ? 'true' : 'false');
      button.disabled = !!disabled;
      button.className = [
        'tw:w-full tw:sm:w-auto tw:flex-grow tw:min-h-10 tw:flex tw:items-center tw:justify-center tw:gap-1 tw:rounded-md tw:border tw:px-3 tw:py-2 tw:text-xs tw:font-medium tw:text-center tw:leading-snug tw:transition-colors tw:duration-200 tw:ease-standard',
        'tw:focus:outline-none tw:focus-visible:ring-2 tw:focus-visible:ring-gray-950 tw:focus-visible:ring-offset-1',
        selected ? 'tw:border-gray-950 tw:bg-gray-950 tw:text-white' : 'tw:border-gray-300 tw:bg-white tw:text-gray-900 tw:hover:bg-gray-50',
        disabled ? 'tw:cursor-not-allowed tw:opacity-40' : 'tw:cursor-pointer'
      ].join(' ');
      var text = document.createElement('span');
      text.textContent = label;
      button.appendChild(text);
      if (price) {
        var amount = document.createElement('span');
        amount.className = 'tw:text-xs tw:font-semibold tw:text-current';
        amount.textContent = price;
        button.appendChild(amount);
      }
      return button;
    }

    function appendAddonStatus(parent, message) {
      var status = document.createElement('p');
      status.className = 'tw:m-0 tw:rounded tw:border tw:border-amber-200 tw:bg-amber-50 tw:p-3 tw:text-xs tw:text-amber-900';
      status.textContent = message;
      parent.appendChild(status);
    }

    function getLidValidationMessage() {
      if (state.lid.mode !== 'CUSTOM') return '';
      var text = String(state.lid.text || '').trim();
      if (!text) return 'Enter lid engraving text, or choose Skip if you do not want lid engraving.';
      if (text.length > getLidMaxLength()) return 'Lid engraving is too long for this size.';
      if (!LID_ALLOWED_PATTERN.test(text)) return 'Use letters, numbers, spaces, and basic punctuation.';
      if (!state.lidMatch.variant) return state.lidMatch.error || 'Lid engraving is not available for this size.';
      return '';
    }

    function renderLidHelp() {
      if (!lidHelp || cfg.lidHelpCopy) return;
      var maxLength = getLidMaxLength();
      lidHelp.textContent = 'Add custom text to the ULTEM lid, up to ' + maxLength + (maxLength === 1 ? ' character.' : ' characters.');
    }

    function renderLidControls() {
      if (!lidControls) return;
      resolveAddons();
      renderLidHelp();
      lidControls.innerHTML = '';
      var group = document.createElement('div');
      group.setAttribute('role', 'radiogroup');
      group.setAttribute('aria-label', 'Lid engraving mode');
      group.className = 'tw:flex tw:w-full tw:flex-wrap tw:gap-1.5';
      group.appendChild(createModeButton(
        'data-lid-mode',
        'CUSTOM',
        'Add Custom Lid Engraving Text',
        formatAddonPrice(state.lidMatch.variant),
        state.lid.mode === 'CUSTOM',
        !state.lidMatch.variant
      ));
      group.appendChild(createModeButton(
        'data-lid-mode',
        'NONE',
        'Skip! No Engraving On the Lid',
        '',
        state.lid.mode === 'NONE',
        false
      ));
      lidControls.appendChild(group);
      if (state.lidMatch.error) appendAddonStatus(lidControls, state.lidMatch.error + ' You can still choose Skip.');

      if (state.lid.mode === 'CUSTOM') {
        var maxLength = getLidMaxLength();
        var label = document.createElement('label');
        label.className = 'tw:flex tw:w-full tw:flex-col tw:gap-1 tw:mb-0';
        var labelText = document.createElement('span');
        labelText.className = 'tw:text-xs tw:font-bold tw:leading-tight tw:text-black';
        labelText.textContent = cfg.lidTitle || 'Lid engraving';
        label.appendChild(labelText);
        var wrap = document.createElement('span');
        wrap.className = 'tw:relative tw:block';
        var input = document.createElement('input');
        input.type = 'text';
        input.setAttribute('data-lid-input', '');
        input.setAttribute('maxlength', String(maxLength));
        input.setAttribute('aria-describedby', 'ultem-v2-lid-validation-' + cfg.sectionId);
        input.placeholder = cfg.lidPlaceholder || '';
        input.value = state.lid.text || '';
        input.className = 'tw:m-0 tw:min-h-[38px] tw:w-full tw:rounded-md tw:border tw:border-gray-300 tw:bg-white tw:pl-3 tw:pr-16 tw:text-xs tw:transition-colors tw:duration-fast tw:ease-standard tw:hover:border-gray-400 tw:focus-visible:ring-black';
        wrap.appendChild(input);
        var counter = document.createElement('span');
        counter.setAttribute('data-lid-counter', '');
        counter.className = 'tw:pointer-events-none tw:absolute tw:right-3 tw:top-1/2 tw:-translate-y-1/2 tw:text-xs tw:tabular-nums tw:text-gray-500';
        counter.textContent = String((state.lid.text || '').length) + ' / ' + maxLength;
        wrap.appendChild(counter);
        label.appendChild(wrap);
        lidControls.appendChild(label);
      }
    }

    function renderPocketHelp() {
      if (!pocketHelp || cfg.pocketHelpCopy) return;
      var kind = getPocketKind();
      if (kind === 'weekly') {
        pocketHelp.textContent = 'Choose the first day for the case; the remaining pocket days follow in sequence.';
      } else if (kind === 'mission') {
        pocketHelp.textContent = 'Choose one of the available three-pocket engraving sets.';
      } else {
        pocketHelp.textContent = 'Choose the label to engrave inside the pocket.';
      }
    }

    // Step 4 preview. The case body is a regular coloured case, so this draws the
    // selected colour's pocket photo and lays the labels over it the same way the
    // BYO picker does.
    var POCKET_PREVIEW_WIDTHS = { nano: '52px', mission: '56px', weekly: '42px' };

    function formatDotwShort(value) {
      var normalized = String(value || '').trim().toLowerCase();
      for (var i = 0; i < DOTW_DAYS.length; i++) {
        if (DOTW_DAYS[i].toLowerCase() === normalized || DOTW_DAYS[i].slice(0, 3).toLowerCase() === normalized) {
          return DOTW_DAYS[i].slice(0, 3).toUpperCase();
        }
      }
      return String(value || '').trim().slice(0, 3).toUpperCase();
    }

    function buildDotwSequence(startValue, count) {
      var start = 0;
      var normalized = String(startValue || '').trim().toLowerCase();
      for (var i = 0; i < DOTW_DAYS.length; i++) {
        if (DOTW_DAYS[i].toLowerCase() === normalized || DOTW_DAYS[i].slice(0, 3).toLowerCase() === normalized) {
          start = i;
          break;
        }
      }
      var values = [];
      for (var j = 0; j < count; j++) values.push(formatDotwShort(DOTW_DAYS[(start + j) % DOTW_DAYS.length]));
      return values;
    }

    function getPocketPreviewLabels() {
      if (state.pocket.mode !== getPocketMode()) return [];
      var value = String(state.pocket.value || '').trim();
      if (!value) return [];
      var kind = getPocketKind();
      if (kind === 'weekly') return buildDotwSequence(value, 7);
      if (kind === 'mission') {
        return value.split('-').map(function (part) { return String(part || '').trim().toUpperCase(); }).filter(Boolean);
      }
      return [value.toUpperCase()];
    }

    function getPocketPreviewSrc(kind) {
      var color = getSelectedColor();
      var row = color && color.key ? pocketImages[color.key] : null;
      return (row && row[kind]) || cfg.pocketFrameImage || '';
    }

    function getPocketPreviewAreas(count, kind) {
      if (kind === 'nano' || count <= 1) return [{ top: '35%', left: '50%', width: '48px' }];
      if (kind === 'mission') return [
        { top: '18%', left: '50%', width: '52px' },
        { top: '50%', left: '50%', width: '52px' },
        { top: '80%', left: '50%', width: '52px' }
      ];
      return [6.5, 18.5, 31, 44, 56.5, 69, 81.5].map(function (top) {
        return { top: top + '%', left: '50%', width: '34px' };
      });
    }

    function createPocketPreviewLabel(text, area, count, kind) {
      var value = String(text || '').trim().toUpperCase();
      // A long nano label runs along the pocket rather than across it.
      var vertical = kind === 'nano' && value.length > 3;
      var label = document.createElement('span');
      label.setAttribute('aria-hidden', 'true');
      label.className = 'tw:absolute tw:flex tw:items-center tw:justify-center tw:truncate tw:text-center tw:leading-none tw:text-neutral-200';
      label.style.left = (area && area.left) || '50%';
      label.style.top = (area && area.top) || '50%';
      label.style.transform = vertical ? 'translate(-50%, 10%) rotate(90deg)' : 'translate(-50%, -50%)';
      label.style.width = vertical ? '64px' : ((area && area.width) || (count > 3 ? '34px' : '48px'));
      label.style.maxWidth = label.style.width;
      label.style.whiteSpace = 'nowrap';
      label.style.fontSize = (vertical ? 16 : 12) + 'px';
      label.textContent = value;
      return label;
    }

    function renderPocketPreview() {
      if (!pocketBodyPreview) return;
      pocketBodyPreview.innerHTML = '';
      pocketBodyPreview.classList.toggle('tw:hidden', !state.size);
      pocketBodyPreview.classList.toggle('tw:flex', !!state.size);
      if (!state.size) return;

      var kind = getPocketKind();
      var labels = getPocketPreviewLabels();
      var frame = document.createElement('span');
      frame.setAttribute('role', 'img');
      frame.setAttribute('aria-label', 'Pocket engraving preview');
      frame.className = 'tw:relative tw:inline-flex tw:shrink-0 tw:items-center tw:justify-center tw:overflow-hidden tw:rounded-sm tw:bg-white';
      frame.style.width = POCKET_PREVIEW_WIDTHS[kind] || '52px';
      frame.style.maxWidth = '100%';

      var src = getPocketPreviewSrc(kind);
      if (src) {
        var img = document.createElement('img');
        img.src = src;
        img.alt = '';
        img.loading = 'lazy';
        img.className = 'tw:block tw:h-auto tw:w-full tw:object-contain';
        frame.appendChild(img);
      } else {
        frame.style.aspectRatio = '1 / 1';
      }

      var areas = getPocketPreviewAreas(labels.length, kind);
      labels.forEach(function (label, index) {
        frame.appendChild(createPocketPreviewLabel(label, areas[index], labels.length, kind));
      });
      pocketBodyPreview.appendChild(frame);
    }

    function renderPocketControls() {
      if (!pocketControls) return;
      resolveAddons();
      renderPocketHelp();
      pocketControls.innerHTML = '';
      var mode = getPocketMode();
      var group = document.createElement('div');
      group.setAttribute('role', 'radiogroup');
      group.setAttribute('aria-label', 'Pocket engraving mode');
      group.className = 'tw:flex tw:w-full tw:flex-wrap tw:gap-1.5';
      group.appendChild(createModeButton(
        'data-pocket-mode',
        mode,
        mode === 'DOTW' ? 'Add Days of the Week Inside the Pockets' : 'Add Custom Pocket Engraving Text',
        formatAddonPrice(state.pocketMatch.variant),
        state.pocket.mode === mode,
        !state.pocketMatch.variant
      ));
      group.appendChild(createModeButton(
        'data-pocket-mode',
        'NONE',
        'Skip! No Pocket Engravings',
        '',
        state.pocket.mode === 'NONE',
        false
      ));
      pocketControls.appendChild(group);
      if (state.pocketMatch.error) appendAddonStatus(pocketControls, state.pocketMatch.error + ' You can still choose Skip.');

      if (state.pocket.mode === mode) {
        var values = getPocketOptions();
        var label = document.createElement('label');
        label.className = 'tw:flex tw:w-full tw:max-w-full tw:flex-col tw:gap-1 tw:sm:w-64 tw:mb-0';
        var labelText = document.createElement('span');
        labelText.className = 'tw:text-xs tw:font-bold tw:text-black';
        labelText.textContent = mode === 'DOTW' ? 'Select start day:' : 'Select from options:';
        label.appendChild(labelText);
        var select = document.createElement('select');
        select.setAttribute('data-pocket-value', '');
        select.className = 'tw:m-0 tw:min-h-10 tw:w-full tw:cursor-pointer tw:rounded-md tw:border tw:border-gray-300 tw:bg-white tw:px-3 tw:py-2 tw:text-xs tw:text-gray-950 tw:focus:outline-none tw:focus-visible:ring-2 tw:focus-visible:ring-gray-950 tw:focus-visible:ring-offset-1';
        values.forEach(function (value) {
          var option = document.createElement('option');
          option.value = value;
          option.textContent = value;
          option.selected = value === state.pocket.value;
          select.appendChild(option);
        });
        label.appendChild(select);
        pocketControls.appendChild(label);
      }
    }

    function updateConfirmButtons() {
      var sizeConfirm = root.querySelector('[data-step-confirm="1"]');
      if (sizeConfirm) sizeConfirm.disabled = !!(state.loading || state.loadError || !state.pendingSize || !state.variant);
      var colorConfirm = root.querySelector('[data-step-confirm="2"]');
      if (colorConfirm) colorConfirm.disabled = !!(!state.size || !state.variant || !state.variant.available);
      var lidConfirm = root.querySelector('[data-step-confirm="3"]');
      if (lidConfirm) lidConfirm.disabled = !!(!state.size || !state.variant);
      var pocketConfirm = root.querySelector('[data-step-confirm="4"]');
      if (pocketConfirm) pocketConfirm.disabled = !!(!state.size || !state.variant);
    }

    function isReady() {
      return !submitting
        && !!(state.variant && state.variant.id && state.variant.available)
        && STEP_ORDER.every(function (step) { return !!state.touched[step]; });
    }

    function renderAddButton() {
      if (!addButton) return;
      var ready = isReady();
      addButton.disabled = !ready;
      if (addLabel) addLabel.textContent = ready ? 'Add to Cart' : 'Configure Your Case First';
      if (!submitting && addSpinner) addSpinner.classList.add('tw:hidden');
    }

    function dispatchState() {
      root.dispatchEvent(new CustomEvent('ultem-v2:statechange', {
        bubbles: true,
        detail: { state: state }
      }));
    }

    function refresh() {
      renderSizeSelector();
      renderColorSelect();
      renderEngravingPreview();
      renderPocketPreview();
      renderPrice();
      renderStock();
      renderCaseSpecs();
      updateConfirmButtons();
      renderAddButton();
      STEP_ORDER.forEach(applyStepVisual);
      dispatchState();
    }

    function confirmStep(step, options) {
      options = options || {};
      if (step === '1') {
        if (!state.pendingSize || state.loading || state.loadError || !state.variant) {
          setValidation('1', state.loadError || 'Choose an available size before confirming.');
          return;
        }
        state.size = state.pendingSize;
        setValidation('1', '');
        setTouched('1', true);
        setSummary('1', state.size.label);
        resolveAddons();
        setLocked('2', false);
        expandStep('2', { focus: options.focus !== false });
      } else if (step === '2') {
        var color = getSelectedColor();
        if (!color || !color.available || !color.variant || !color.variant.available) {
          setValidation('2', 'Choose an available color before confirming.');
          return;
        }
        state.variant = color.variant;
        root.setAttribute('data-selected-variant-id', state.variant.id || '');
        setValidation('2', '');
        setTouched('2', true);
        setSummary('2', color.name);
        resolveAddons();
        setLocked('3', false);
        expandStep('3', { focus: options.focus !== false });
      } else if (step === '3') {
        if (!state.lid.mode) {
          setValidation('3', 'Choose Add Custom Lid Engraving Text or Skip before confirming.');
          return;
        }
        var lidError = state.lid.mode === 'CUSTOM' ? getLidValidationMessage() : '';
        if (lidError) {
          setValidation('3', lidError);
          return;
        }
        setValidation('3', '');
        setTouched('3', true);
        setSummary('3', state.lid.mode === 'CUSTOM' ? state.lid.text.trim() : 'No engraving');
        setLocked('4', false);
        expandStep('4', { focus: options.focus !== false });
      } else if (step === '4') {
        if (!state.pocket.mode) {
          setValidation('4', 'Choose a pocket engraving option or Skip before confirming.');
          return;
        }
        if (state.pocket.mode !== 'NONE') {
          if (!state.pocketMatch.variant) {
            setValidation('4', state.pocketMatch.error || 'Pocket engraving is not available for this size.');
            return;
          }
          if (getPocketOptions().indexOf(state.pocket.value) === -1) {
            setValidation('4', 'Choose a valid pocket engraving option.');
            return;
          }
        }
        setValidation('4', '');
        setTouched('4', true);
        setSummary('4', state.pocket.mode === 'NONE' ? 'No engraving' : state.pocket.value);
        collapseAll();
      }
      refresh();
    }

    function getCurrencyCode() {
      if (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) {
        return String(window.Shopify.currency.active || '').trim().toUpperCase();
      }
      return String(cfg.cartCurrency || '').trim().toUpperCase();
    }

    function buildCaseMeta(variant) {
      var currency = getCurrencyCode();
      var cents = parseCents(variant && variant.price);
      return currency && cents > 0 ? currency + ':' + (cents / 100).toFixed(2) : '';
    }

    function buildCartItems() {
      if (!isReady()) return null;
      var bundleId = Date.now();
      var marker = 'com2:' + bundleId;
      var properties = { _ikg_bundle: marker };
      var caseMeta = buildCaseMeta(state.variant);
      var selectedColor = getSelectedColor();
      if (caseMeta) properties._ikg_case = caseMeta;
      if (selectedColor && selectedColor.name) properties.Color = selectedColor.name;

      if (state.lid.mode === 'CUSTOM' && state.lid.text.trim()) {
        properties['Lid Engraving'] = state.lid.text.trim();
      }
      if (state.pocket.mode !== 'NONE' && state.pocket.value) {
        properties[getPocketKind() === 'weekly' ? 'DOTW' : 'Pocket Engraving'] = state.pocket.value;
      }

      var items = [{
        id: parseInt(state.variant.id, 10) || state.variant.id,
        quantity: 1,
        properties: properties
      }];
      if (state.lid.mode === 'CUSTOM' && state.lid.text.trim() && state.lidMatch.variant) {
        items.push({
          id: parseInt(state.lidMatch.variant.id, 10) || state.lidMatch.variant.id,
          quantity: 1,
          properties: { _ikg_bundle: marker + ':lid' }
        });
      }
      if (state.pocket.mode !== 'NONE' && state.pocket.value && state.pocketMatch.variant) {
        items.push({
          id: parseInt(state.pocketMatch.variant.id, 10) || state.pocketMatch.variant.id,
          quantity: 1,
          properties: { _ikg_bundle: marker + ':pocket' }
        });
      }
      return items;
    }

    function cartRoute(path) {
      var base = window.Shopify && window.Shopify.routes && window.Shopify.routes.root
        ? window.Shopify.routes.root
        : '/';
      return String(base).replace(/\/?$/, '/') + String(path || '').replace(/^\/+/, '');
    }

    function setAddError(message) {
      var text = String(message || '').trim();
      if (addMessage) addMessage.textContent = text;
      if (addError) {
        addError.classList.toggle('tw:hidden', !text);
        addError.setAttribute('aria-hidden', text ? 'false' : 'true');
      }
    }

    function handleAddToCart() {
      if (!addButton || addButton.disabled || submitting) return;
      var items = buildCartItems();
      if (!items || !items.length) {
        setAddError('Complete each step to continue.');
        return;
      }

      submitting = true;
      addButton.disabled = true;
      if (addLabel) addLabel.textContent = 'Adding...';
      if (addSpinner) addSpinner.classList.remove('tw:hidden');
      setAddError('');

      if (window.console && typeof console.log === 'function') {
        console.log('[ultem-v2] cart payload', items.slice().reverse());
      }
      fetch(cartRoute('cart/add.js'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ items: items.slice().reverse() })
      })
        .then(function (response) {
          if (response.ok) return response.json();
          return response.json().catch(function () { return {}; }).then(function (payload) {
            throw new Error(payload.description || payload.message || 'Could not add this item to cart.');
          });
        })
        .then(function () {
          window.location.href = cartRoute('cart');
        })
        .catch(function (error) {
          submitting = false;
          if (addSpinner) addSpinner.classList.add('tw:hidden');
          setAddError(error && error.message ? error.message : 'Could not add this item to cart.');
          refresh();
        });
    }

    function closeTooltip() {
      if (tooltip && tooltip.parentNode) tooltip.parentNode.removeChild(tooltip);
      tooltip = null;
      tooltipTrigger = null;
    }

    function openTooltip(trigger) {
      var url = trigger && trigger.getAttribute('data-tooltip');
      if (!url) return;
      if (tooltipTrigger === trigger) {
        closeTooltip();
        return;
      }
      closeTooltip();
      tooltip = document.createElement('div');
      tooltip.className = 'dynamic-tooltip';
      tooltip.style.backgroundImage = 'url("' + String(url).replace(/"/g, '\\"') + '")';
      tooltip.style.position = 'absolute';
      tooltip.style.display = 'none';
      document.body.appendChild(tooltip);
      tooltipTrigger = trigger;

      var activeTooltip = tooltip;
      var viewportWidth = window.innerWidth || document.documentElement.clientWidth || 0;
      var isMobile = viewportWidth <= 768;
      var imageWidth = isMobile ? viewportWidth * 0.85 : 750;
      var sourceImage = new Image();
      sourceImage.onload = function () {
        if (tooltip !== activeTooltip) return;
        var naturalWidth = sourceImage.naturalWidth || sourceImage.width || imageWidth;
        var naturalHeight = sourceImage.naturalHeight || sourceImage.height || imageWidth;
        if (!isMobile && naturalWidth < imageWidth) imageWidth = naturalWidth;
        var imageHeight = (imageWidth / naturalWidth) * naturalHeight;
        var rect = trigger.getBoundingClientRect();
        var scrollTop = window.pageYOffset || document.documentElement.scrollTop || 0;

        activeTooltip.style.width = imageWidth + 'px';
        activeTooltip.style.height = imageHeight + 'px';
        activeTooltip.style.top = rect.top + scrollTop + 20 + 'px';
        activeTooltip.style.left = Math.max((viewportWidth - imageWidth) / 2, 0) + 'px';
        activeTooltip.style.display = 'block';
      };
      sourceImage.onerror = function () {
        if (tooltip === activeTooltip) closeTooltip();
      };
      sourceImage.src = url;
    }

    function parseUrlPreselect() {
      try {
        var raw = new URLSearchParams(window.location.search).get('size');
        if (!raw) return null;
        var parts = String(raw).split(':');
        return { sizeKey: normalize(parts[0]), colorKey: normalize(parts[1]) };
      } catch (error) {
        return null;
      }
    }

    function onRootClick(event) {
      var target = event.target;
      if (!target || typeof target.closest !== 'function') return;
      var thumb = target.closest('[data-gallery-thumb]');
      if (thumb && root.contains(thumb)) {
        event.preventDefault();
        setActiveGallerySlide(thumb.getAttribute('data-slide-target'));
        return;
      }
      if (target.closest('[data-gallery-prev]')) {
        event.preventDefault();
        moveGallery(-1);
        return;
      }
      if (target.closest('[data-gallery-next]')) {
        event.preventDefault();
        moveGallery(1);
        return;
      }
      var tooltipButton = target.closest('[data-compare-tooltip]');
      if (tooltipButton && root.contains(tooltipButton)) {
        event.preventDefault();
        event.stopPropagation();
        openTooltip(tooltipButton);
        return;
      }
      var retryButton = target.closest('[data-size-retry]');
      if (retryButton && root.contains(retryButton) && state.pendingSize) {
        event.preventDefault();
        var fetcher = window.theme && window.theme.productDataFetcher;
        if (fetcher && typeof fetcher.clear === 'function') fetcher.clear();
        fetchSizeState(state.pendingSize, { preferredColorKey: state.colorKey });
        return;
      }
      var selectorTrigger = target.closest('[data-selector-trigger]');
      if (selectorTrigger && root.contains(selectorTrigger)) {
        var popoverId = selectorTrigger.getAttribute('popovertarget');
        var selectorPopover = popoverId ? document.getElementById(popoverId) : null;
        if (selectorPopover) placeSelectorPopover(selectorPopover);
        return;
      }
      var sizeOption = target.closest('[data-size-option-id]');
      if (sizeOption && root.contains(sizeOption)) {
        event.preventDefault();
        if (sizeOption.disabled) return;
        var size = getSizeById(sizeOption.getAttribute('data-size-option-id'));
        if (!size) return;
        closeSelectorPopover(sizeOption.closest('[data-selector-popover]'));
        fetchSizeState(size, { preferredColorKey: state.colorKey });
        focusElementSoon(root.querySelector('[data-size-trigger]'));
        return;
      }
      var colorOption = target.closest('[data-color-option-key]');
      if (colorOption && root.contains(colorOption)) {
        event.preventDefault();
        if (colorOption.getAttribute('aria-disabled') === 'true') return;
        var color = getColorByKey(colorOption.getAttribute('data-color-option-key'));
        if (!color || !color.available || !color.variant) return;
        closeSelectorPopover(colorOption.closest('[data-selector-popover]'));
        var colorChanged = state.colorKey !== color.key;
        state.colorKey = color.key;
        state.variant = color.variant;
        root.setAttribute('data-selected-variant-id', state.variant.id || '');
        if (colorChanged && state.touched['2']) resetForColorChange();
        setValidation('2', '');
        // Deliberately keep the selected-size media when only Color changes.
        refresh();
        focusElementSoon(root.querySelector('[data-color-popover-trigger]'));
        return;
      }
      var lidMode = target.closest('[data-lid-mode]');
      if (lidMode && root.contains(lidMode)) {
        event.preventDefault();
        if (lidMode.disabled) return;
        var nextLidMode = lidMode.getAttribute('data-lid-mode');
        if (state.touched['3']) {
          setTouched('3', false);
          setSummary('3', '');
          resetStepsAfter('3');
        }
        state.lid.mode = nextLidMode;
        if (nextLidMode === 'NONE') state.lid.text = '';
        setValidation('3', '');
        renderLidControls();
        refresh();
        return;
      }
      var pocketMode = target.closest('[data-pocket-mode]');
      if (pocketMode && root.contains(pocketMode)) {
        event.preventDefault();
        if (pocketMode.disabled) return;
        var nextPocketMode = pocketMode.getAttribute('data-pocket-mode');
        if (state.touched['4']) {
          setTouched('4', false);
          setSummary('4', '');
        }
        state.pocket.mode = nextPocketMode;
        state.pocket.value = nextPocketMode === 'NONE' ? '' : (getPocketOptions()[0] || '');
        setValidation('4', '');
        renderPocketControls();
        refresh();
        return;
      }
      if (target.closest('[data-add-to-cart]')) {
        event.preventDefault();
        handleAddToCart();
        return;
      }
      var confirm = target.closest('[data-step-confirm]');
      if (confirm && root.contains(confirm)) {
        event.preventDefault();
        if (!confirm.disabled) confirmStep(confirm.getAttribute('data-step-confirm'));
        return;
      }
      var header = target.closest('[data-step-header]');
      if (header && root.contains(header)) {
        event.preventDefault();
        var step = header.getAttribute('data-step-header');
        if (header.disabled) return;
        if (state.expanded === step) confirmStep(step);
        else expandStep(step);
      }
    }

    function onRootChange(event) {
      var target = event.target;
      if (!target || typeof target.closest !== 'function') return;
      var pocketValue = target.closest('[data-pocket-value]');
      if (pocketValue && root.contains(pocketValue)) {
        state.pocket.value = pocketValue.value;
        if (state.touched['4']) {
          setTouched('4', false);
          setSummary('4', '');
        }
        setValidation('4', '');
        refresh();
      }
    }

    function onRootInput(event) {
      var input = event.target && event.target.closest ? event.target.closest('[data-lid-input]') : null;
      if (!input || !root.contains(input)) return;
      var maxLength = getLidMaxLength();
      if (input.value.length > maxLength) input.value = input.value.slice(0, maxLength);
      state.lid.mode = 'CUSTOM';
      state.lid.text = input.value;
      var counter = lidControls && lidControls.querySelector('[data-lid-counter]');
      if (counter) counter.textContent = String(input.value.length) + ' / ' + maxLength;
      if (state.touched['3']) {
        setTouched('3', false);
        setSummary('3', '');
        resetStepsAfter('3');
      }
      setValidation('3', getLidValidationMessage());
      refresh();
    }

    function onRootKeydown(event) {
      if (!event.target || !event.target.closest) return;
      var selectorOption = event.target.closest('[data-selector-option]');
      if (selectorOption && root.contains(selectorOption)) {
        var popover = selectorOption.closest('[data-selector-popover]');
        var options = getEnabledSelectorOptions(popover);
        var currentIndex = options.indexOf(selectorOption);
        if (currentIndex < 0) currentIndex = 0;
        if (event.key === 'ArrowDown' || event.key === 'ArrowRight') {
          event.preventDefault();
          if (options.length) focusSelectorOption(options[(currentIndex + 1 + options.length) % options.length]);
          return;
        }
        if (event.key === 'ArrowUp' || event.key === 'ArrowLeft') {
          event.preventDefault();
          if (options.length) focusSelectorOption(options[(currentIndex - 1 + options.length) % options.length]);
          return;
        }
        if (event.key === 'Home' || event.key === 'End') {
          event.preventDefault();
          if (options.length) focusSelectorOption(event.key === 'Home' ? options[0] : options[options.length - 1]);
          return;
        }
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          selectorOption.click();
          return;
        }
        if (event.key === 'Escape') {
          event.preventDefault();
          var trigger = getSelectorPopoverTrigger(popover);
          closeSelectorPopover(popover);
          focusElementSoon(trigger);
          return;
        }
      }
      var selectorTrigger = event.target.closest('[data-selector-trigger]');
      if (selectorTrigger && root.contains(selectorTrigger) && (event.key === 'ArrowDown' || event.key === 'ArrowUp')) {
        event.preventDefault();
        openSelectorPopoverFromTrigger(selectorTrigger, event.key === 'ArrowUp');
        return;
      }
      if (event.target.closest('[data-gallery-stage]')) {
        if (event.key === 'ArrowLeft') {
          event.preventDefault();
          moveGallery(-1);
        } else if (event.key === 'ArrowRight') {
          event.preventDefault();
          moveGallery(1);
        }
      }
    }

    function onPointerDown(event) {
      swipeState = { id: event.pointerId, x: event.clientX, y: event.clientY };
    }

    function onPointerUp(event) {
      if (!swipeState || swipeState.id !== event.pointerId) return;
      var dx = event.clientX - swipeState.x;
      var dy = event.clientY - swipeState.y;
      swipeState = null;
      if (Math.abs(dx) >= 48 && Math.abs(dx) > Math.abs(dy) * 1.2) moveGallery(dx < 0 ? 1 : -1);
    }

    function onDocumentClick(event) {
      var target = event.target;
      if (tooltip && (!target || typeof target.closest !== 'function' || !target.closest('[data-compare-tooltip], .dynamic-tooltip'))) {
        closeTooltip();
      }
    }

    root.addEventListener('click', onRootClick);
    root.addEventListener('change', onRootChange);
    root.addEventListener('input', onRootInput);
    root.addEventListener('keydown', onRootKeydown);
    document.addEventListener('click', onDocumentClick);
    if (galleryStage) {
      galleryStage.addEventListener('pointerdown', onPointerDown);
      galleryStage.addEventListener('pointerup', onPointerUp);
      galleryStage.addEventListener('pointercancel', function () { swipeState = null; });
    }

    STEP_ORDER.forEach(function (step) {
      setLocked(step, step !== '1');
      setTouched(step, false);
    });
    setActiveGallerySlide(0);
    renderSizeSelector();
    renderColorSelect();
    expandStep('1');

    var preselect = parseUrlPreselect();
    var preselectedSize = preselect ? getSizeByKey(preselect.sizeKey) : null;
    if (preselectedSize && preselectedSize.available) {
      fetchSizeState(preselectedSize, {
        preferredColorKey: preselect.colorKey,
        autoConfirm: true,
        autoConfirmColor: !!preselect.colorKey
      });
    }

    window.theme.bundleBuilder = window.theme.bundleBuilder || {};
    window.theme.bundleBuilder[cfg.sectionId] = {
      state: state,
      expandStep: expandStep,
      confirmStep: confirmStep,
      buildCartItems: buildCartItems,
      handleAddToCart: handleAddToCart
    };

    return {
      root: root,
      destroy: function () {
        destroyed = true;
        loadToken += 1;
        closeTooltip();
        closeSelectorPopover(openSizePopover);
        closeSelectorPopover(openColorPopover);
        unbindSelectorPopoverListeners();
        root.removeEventListener('click', onRootClick);
        root.removeEventListener('change', onRootChange);
        root.removeEventListener('input', onRootInput);
        root.removeEventListener('keydown', onRootKeydown);
        document.removeEventListener('click', onDocumentClick);
        if (galleryStage) {
          galleryStage.removeEventListener('pointerdown', onPointerDown);
          galleryStage.removeEventListener('pointerup', onPointerUp);
        }
        if (window.theme.bundleBuilder && window.theme.bundleBuilder[cfg.sectionId]) {
          delete window.theme.bundleBuilder[cfg.sectionId];
        }
      }
    };
  }

  function init(cfg) {
    if (!cfg || cfg.sectionId === null || cfg.sectionId === undefined) return null;
    var sectionId = String(cfg.sectionId);
    var root = document.getElementById('ultem-v2-builder-' + sectionId);
    if (!root) return null;
    if (instances[sectionId] && instances[sectionId].root === root) return instances[sectionId];
    if (instances[sectionId]) instances[sectionId].destroy();
    cfg.sectionId = sectionId;
    instances[sectionId] = createInstance(cfg);
    return instances[sectionId];
  }

  function initFrom(scope) {
    var context = scope && typeof scope.querySelectorAll === 'function' ? scope : document;
    var configs = [];
    if (context.matches && context.matches(CONFIG_SELECTOR)) configs.push(context);
    var descendants = context.querySelectorAll(CONFIG_SELECTOR);
    for (var i = 0; i < descendants.length; i++) configs.push(descendants[i]);
    for (var j = 0; j < configs.length; j++) {
      var cfg = parseConfig(configs[j]);
      if (cfg) init(cfg);
    }
  }

  function destroy(sectionId) {
    var key = String(sectionId || '');
    if (!instances[key]) return;
    instances[key].destroy();
    delete instances[key];
  }

  window.theme.ultemV2 = { init: init, initFrom: initFrom, destroy: destroy };
  document.addEventListener('shopify:section:load', function (event) { initFrom(event.target); });
  document.addEventListener('shopify:section:unload', function (event) {
    if (event.detail && event.detail.sectionId) destroy(event.detail.sectionId);
  });

  function boot() { initFrom(document); }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot, { once: true });
  else boot();
})();
